import { Vec3, add, sub, scale, cross, dot, ZERO } from '../math/vec3'
import { Quat, quat, normalizeQuat, mulQuat, rotate } from '../math/quat'
import { Mat3, mulVec3, inverse } from '../math/mat3'

export interface RigidState {
  /** posizione nel sistema del corpo celeste centrale, m */
  readonly position: Vec3
  /** velocita nello stesso sistema, m/s */
  readonly velocity: Vec3
  /** orientamento corpo → mondo */
  readonly orientation: Quat
  /** velocita angolare in SISTEMA CORPO, rad/s */
  readonly angularVelocity: Vec3
}

export interface Wrench {
  /** forza risultante in sistema MONDO, N */
  readonly force: Vec3
  /** coppia risultante in sistema CORPO, attorno al centro di massa, N·m */
  readonly torque: Vec3
}

export const ZERO_WRENCH: Wrench = { force: ZERO, torque: ZERO }

export interface InertialProperties {
  readonly mass: number
  readonly inertia: Mat3
  readonly inverseInertia: Mat3
}

export function inertialProperties(mass: number, inertia: Mat3): InertialProperties {
  return { mass, inertia, inverseInertia: inverse(inertia) }
}

export type WrenchFn = (state: RigidState, t: number) => Wrench

/** Derivata dello stato: le quattro componenti del quaternione viaggiano come numeri. */
interface Derivative {
  readonly dPosition: Vec3
  readonly dVelocity: Vec3
  readonly dOrientation: Quat
  readonly dAngularVelocity: Vec3
}

function derivative(state: RigidState, props: InertialProperties, w: Wrench): Derivative {
  // Equazione di Eulero: I ω̇ = τ − ω × (I ω)
  const Iw = mulVec3(props.inertia, state.angularVelocity)
  const gyro = cross(state.angularVelocity, Iw)
  const dOmega = mulVec3(props.inverseInertia, sub(w.torque, gyro))

  // q̇ = ½ q ⊗ (0, ω)
  const omegaQuat = quat(state.angularVelocity.x, state.angularVelocity.y, state.angularVelocity.z, 0)
  const dq = mulQuat(state.orientation, omegaQuat)

  return {
    dPosition: state.velocity,
    dVelocity: scale(w.force, 1 / props.mass),
    dOrientation: quat(dq.x * 0.5, dq.y * 0.5, dq.z * 0.5, dq.w * 0.5),
    dAngularVelocity: dOmega,
  }
}

function advance(state: RigidState, d: Derivative, dt: number): RigidState {
  return {
    position: add(state.position, scale(d.dPosition, dt)),
    velocity: add(state.velocity, scale(d.dVelocity, dt)),
    orientation: quat(
      state.orientation.x + d.dOrientation.x * dt,
      state.orientation.y + d.dOrientation.y * dt,
      state.orientation.z + d.dOrientation.z * dt,
      state.orientation.w + d.dOrientation.w * dt,
    ),
    angularVelocity: add(state.angularVelocity, scale(d.dAngularVelocity, dt)),
  }
}

const combine = (a: Derivative, b: Derivative, c: Derivative, d: Derivative): Derivative => {
  const w = (p: Vec3, q: Vec3, r: Vec3, s: Vec3): Vec3 =>
    scale(add(add(p, scale(q, 2)), add(scale(r, 2), s)), 1 / 6)
  return {
    dPosition: w(a.dPosition, b.dPosition, c.dPosition, d.dPosition),
    dVelocity: w(a.dVelocity, b.dVelocity, c.dVelocity, d.dVelocity),
    dAngularVelocity: w(a.dAngularVelocity, b.dAngularVelocity, c.dAngularVelocity, d.dAngularVelocity),
    dOrientation: quat(
      (a.dOrientation.x + 2 * b.dOrientation.x + 2 * c.dOrientation.x + d.dOrientation.x) / 6,
      (a.dOrientation.y + 2 * b.dOrientation.y + 2 * c.dOrientation.y + d.dOrientation.y) / 6,
      (a.dOrientation.z + 2 * b.dOrientation.z + 2 * c.dOrientation.z + d.dOrientation.z) / 6,
      (a.dOrientation.w + 2 * b.dOrientation.w + 2 * c.dOrientation.w + d.dOrientation.w) / 6,
    ),
  }
}

/**
 * Un passo di Runge-Kutta del quarto ordine.
 * La massa e l'inerzia sono costanti DENTRO il passo: il propellente viene
 * scalato una volta sola a fine passo, non quattro volte dentro l'integratore.
 */
export function stepRK4(
  state: RigidState,
  props: InertialProperties,
  wrenchFn: WrenchFn,
  t: number,
  dt: number,
): RigidState {
  const k1 = derivative(state, props, wrenchFn(state, t))
  const s2 = advance(state, k1, dt / 2)
  const k2 = derivative(s2, props, wrenchFn(s2, t + dt / 2))
  const s3 = advance(state, k2, dt / 2)
  const k3 = derivative(s3, props, wrenchFn(s3, t + dt / 2))
  const s4 = advance(state, k3, dt)
  const k4 = derivative(s4, props, wrenchFn(s4, t + dt))

  const next = advance(state, combine(k1, k2, k3, k4), dt)
  return { ...next, orientation: normalizeQuat(next.orientation) }
}

/** Momento angolare in sistema mondo: si conserva quando non ci sono coppie. */
export function angularMomentumWorld(state: RigidState, props: InertialProperties): Vec3 {
  return rotate(state.orientation, mulVec3(props.inertia, state.angularVelocity))
}

/** Energia cinetica totale, traslazione piu rotazione. */
export function kineticEnergy(state: RigidState, props: InertialProperties): number {
  const lin = 0.5 * props.mass * dot(state.velocity, state.velocity)
  const rot = 0.5 * dot(state.angularVelocity, mulVec3(props.inertia, state.angularVelocity))
  return lin + rot
}
